import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchEntityDetail } from "../../../components/global-entities/globalEntitiesSlice";
import {
  fetchDocumentFolders,
  setBlobFilterStates,
} from "../../Process/actions/attachmentsSlice";
import apiService from "~/services/service";
import z from "zod";
import API_ROUTES from "~/api-routes";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { fetchDirectors } from "~/components/directors-form/directorsSlice";
import Category from "~/components/Processes/conversations/utils/categories.json";
import React from "react";
import FileDocumentsBreadcrumbContext from "../FileDocumentsBreadcrumbContext";

/** Build category folders from documents: only categories with at least one document. No file count on folder (per ticket). */
function buildCategoryFoldersFromDocuments(documents, categoryList = Category) {
  if (!documents?.length) return [];
  const byCategory = {};
  documents.forEach((doc) => {
    const code = doc?.category || doc?.metadata?.category;
    if (code) {
      byCategory[code] = (byCategory[code] || 0) + 1;
    }
  });
  return Object.entries(byCategory)
    .filter(([, count]) => count > 0)
    .map(([code]) => {
      const cat = categoryList.find((c) => c.code === code);
      return {
        id: code,
        code,
        name: cat?.name ?? code,
        isCategoryFolder: true,
      };
    });
}

/** Fetch documents for a given id and type, optionally filtered by category tag */
async function fetchDocumentsForType(entityId, type, categoryCode = null) {
  let url = API_ROUTES.FETCH_DOCUMENTS;
  if (categoryCode) {
    url += `&tag=category-${encodeURIComponent(categoryCode)}`;
  }
  const { data } = await apiService(url, "GET", {
    replace: { id: entityId, type },
    // Ensure we always have the most recent documents after mutations.
    skipCache: true,
  });
  return data?.filter((f) => f?.createdByData || f?.name) || [];
}

const useFileDocuments = ({
  id,
  service_id = null,
  process_id = null,
  type = null,
  request_type = null,
  request_id = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(setBlobFilterStates({}));
  }, []);
  const { entity_detail, document_folders, processAttachments } = useSelector(
    (state) => ({
      entity_detail: state.globalEntities.entity_detail,
      document_folders: state.attachments.document_folders,
      processAttachments: state.attachments.processAttachments,
    }),
  );

  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [processName, setProcessName] = useState(null);
  const entityData = useSelector((state) => state.globalEntities.entity_detail);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [requests, setRequests] = useState([]);
  const [selectedRequestType, setSelectedRequestType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isPayslipFolder, setIsPayslipFolder] = useState(false);
  const [localSelectedCategory, setLocalSelectedCategory] = useState(null);
  const breadcrumbContext = React.useContext(FileDocumentsBreadcrumbContext);
  const selectedCategory =
    breadcrumbContext?.selectedCategory ?? localSelectedCategory;
  const setSelectedCategory =
    breadcrumbContext?.setSelectedCategory ?? setLocalSelectedCategory;
  const [categoryFoldersLoading, setCategoryFoldersLoading] = useState(false);
  const [categoryFoldersRefetch, setCategoryFoldersRefetch] = useState(0);
  const { value: isPayslipUploadFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PAYSLIP_UPLOAD,
    false,
  );
  const [categoryRefreshKey, setCategoryRefreshKey] = useState(0);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(fetchEntityDetail(id)),
        dispatch(fetchDocumentFolders(id)),
        fetchRequests(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, [id, dispatch]);

  useEffect(() => {
    if (service_id && type && request_type && request_id) {
      setSelectedRequest(null);
    } else if (type && service_id && (process_id || request_type)) {
      setSelectedProcess(null);
      setSelectedRequestType(null);
    } else if (type && service_id) {
      setSelectedType(null);
    } else if (service_id) {
      setSelectedService(null);
    }
  }, [type, service_id, process_id, request_type, request_id]);

  useEffect(() => {
    if (location.pathname.includes("payslips")) {
      setIsPayslipFolder(true);
    } else {
      setIsPayslipFolder(false);
    }
  }, [location.pathname]);

  // Clear selected category when navigating (type, process_id, or request_id change)
  useEffect(() => {
    setSelectedCategory(null);
  }, [type, process_id, request_id]);

  const hasEntityPlusAccess = entityData?.active_services?.some(
    (e) => e.name === "Entities +",
  );

  const requestTypeSubFolders = [
    {
      name: "Service Query",
      code: "service-query",
      id: "SERVICE_QUERY",
    },
    {
      name: "New / Additional Service Request",
      code: "additional-service-request",
      id: "ADDITIONAL_SERVICE_REQUEST",
    },
    {
      name: "Contract / Fees Query",
      code: "fees-query",
      id: "FEES_QUERY",
    },
    {
      name: "Tech Support (IT/Helpdesk)",
      code: "tech-support",
      id: "TECH_SUPPORT",
    },
  ];

  useEffect(() => {
    const foldersdata = document_folders.map((folder) => ({
      ...folder,
      isProcess: true,
    }));
    if (hasEntityPlusAccess) {
      let entityobj = {
        id: "a9c07a03-326c-45e5-b644-a795f0cbe67r",
        name: "Core Entity Information",
        count: 0,
        type: [
          {
            name: "Entity Information",
            code: "entity-information",
            type: "ENTITIES_PLUS",
            subFolders: null,
          },
          {
            name: "Shareholder",
            code: "shareholder",
            type: "SHAREHOLDER",
            subFolders: null,
          },
          {
            name: "Officers",
            code: "officers",
            type: "OFFICER",
            subFolders: null,
          },
          {
            name: "Power of Attorney",
            code: "power-of-attorney",
            type: "OFFICER",
            subFolders: null,
          },
          {
            name: "Directors",
            code: "directors",
            type: "DIRECTOR",
            subFolders: null,
          },
        ],
      };
      foldersdata?.unshift(entityobj);
    }
    let adhoc = {
      id: "a9c07a03-326c-45e5-b644-a795f0cbe67f",
      name: "Ad Hoc Requests",
      count: 0,
      process: null,
    };
    foldersdata?.push(adhoc);

    if (!service_id) {
      setFolders(foldersdata);
    } else if (selectedService?.name === "Payroll" && selectedProcess) {
      const subFolder = isPayslipUploadFeatureEnabled
        ? selectedProcess?.subFolders
        : selectedProcess?.subFolders?.filter((folder) => {
            folder?.name !== "Payslips";
          });
      setFolders(subFolder);
    } else {
      const serviceFolder = foldersdata?.find((f) => f.id === service_id);
      if (serviceFolder?.name === "Core Entity Information") {
        setSelectedService(serviceFolder);
        if (!type) {
          setFolders(serviceFolder?.type || []);
        } else {
          const selectedtype = serviceFolder?.type?.find((f) => f.code == type);
          setSelectedType(selectedtype);
          if (selectedtype?.code === "entity-information") {
            fetchDocumentsForType(id, "ENTITIES_PLUS")
              .then((docs) => buildCategoryFoldersFromDocuments(docs))
              .then(setFolders)
              .catch(() => setFolders([]));
          } else {
            getUserList(!!process_id);
          }
        }
      } else if (serviceFolder?.name === "Ad Hoc Requests") {
        if (requests.length === 0) {
          setSelectedService(serviceFolder);
          setFolders([]);
          return;
        }
        const grouped = requests?.reduce((acc, req) => {
          const year = new Date(req.createdAt).getFullYear();
          if (!acc[year]) {
            acc[year] = {
              name: year,
              type: "Ad Hoc Requests",
              requestTypeFolders: requestTypeSubFolders,
            };
          }
          return acc;
        }, {});
        const groupedArray = Object.values(grouped || {});
        setSelectedService(serviceFolder);
        if (!type) {
          setFolders(groupedArray);
        } else if (request_type) {
          setSelectedType(groupedArray?.find((f) => f.name == type));
          if (!request_id) setRequestFolders();
        } else {
          const selectedyear = groupedArray?.find((f) => f.name == type);
          setSelectedType(selectedyear);
          setFolders(selectedyear?.requestTypeFolders);
        }
      } else {
        const grouped = serviceFolder?.process?.reduce((acc, process) => {
          const year = new Date(process.start_date).getFullYear();
          if (!acc[year]) {
            acc[year] = {
              name: year,
              count: 0,
              isProcess: true,
              process: [],
            };
          }
          acc[year].count += process.count;
          acc[year].process.push({ ...process, isProcess: true });
          return acc;
        }, {});
        const groupedArray = Object.values(grouped || {});
        setSelectedService(serviceFolder);

        if (!type) {
          setFolders(groupedArray);
        } else {
          const selectedyear = groupedArray?.find((f) => f.name == type);
          setSelectedType(selectedyear);
          if (!process_id) setFolders(selectedyear?.process);
        }
      }
    }
  }, [
    document_folders,
    requests,
    process_id,
    categoryRefreshKey,
    categoryFoldersRefetch,
  ]);

  useEffect(() => {
    if (
      !process_id ||
      !selectedService ||
      selectedService?.name !== "Core Entity Information" ||
      !selectedProcess?.id ||
      !selectedType?.type
    )
      return;
    let cancelled = false;
    setCategoryFoldersLoading(true);
    setFolders([]);
    fetchDocumentsForType(selectedProcess.id, selectedType.type)
      .then((docs) =>
        cancelled ? [] : buildCategoryFoldersFromDocuments(docs),
      )
      .then((categoryFolders) => {
        if (!cancelled) setFolders(categoryFolders);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      })
      .finally(() => {
        if (!cancelled) setCategoryFoldersLoading(false);
      });
    return () => {
      cancelled = true;
      setCategoryFoldersLoading(false);
    };
  }, [
    process_id,
    selectedService,
    selectedProcess?.id,
    selectedType?.type,
    categoryFoldersRefetch,
    categoryRefreshKey,
  ]);

  useEffect(() => {
    if (
      !process_id ||
      !selectedService ||
      selectedService?.name === "Core Entity Information" ||
      !selectedProcess?.id
    )
      return;
    let cancelled = false;
    setCategoryFoldersLoading(true);
    setFolders([]);
    fetchDocumentsForType(process_id, "PROCESS")
      .then((docs) =>
        cancelled ? [] : buildCategoryFoldersFromDocuments(docs),
      )
      .then((categoryFolders) => {
        if (!cancelled) setFolders(categoryFolders);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      })
      .finally(() => {
        if (!cancelled) setCategoryFoldersLoading(false);
      });
    return () => {
      cancelled = true;
      setCategoryFoldersLoading(false);
    };
  }, [
    process_id,
    selectedService,
    selectedProcess?.id,
    categoryFoldersRefetch,
    categoryRefreshKey,
  ]);

  useEffect(() => {
    if (
      !request_id ||
      !selectedService ||
      selectedService?.name !== "Ad Hoc Requests" ||
      !selectedRequest?.id
    )
      return;
    let cancelled = false;
    setCategoryFoldersLoading(true);
    setFolders([]);
    fetchDocumentsForType(request_id, "AD_HOC")
      .then((docs) =>
        cancelled ? [] : buildCategoryFoldersFromDocuments(docs),
      )
      .then((categoryFolders) => {
        if (!cancelled) setFolders(categoryFolders);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      })
      .finally(() => {
        if (!cancelled) setCategoryFoldersLoading(false);
      });
    return () => {
      cancelled = true;
      setCategoryFoldersLoading(false);
    };
  }, [
    request_id,
    selectedService,
    selectedRequest?.id,
    categoryFoldersRefetch,
    categoryRefreshKey,
  ]);

  const setRequestFolders = () => {
    const key = Object.keys(reasonTypes).find(
      (key) => key.toLowerCase() === request_type.toLowerCase(),
    );
    const value = reasonTypes[key];

    const requestSubFolders = requests
      .filter((r) => r.reason === value)
      .map((r) => ({
        ...r,
        name: r.request_id,
      }));

    setFolders(requestSubFolders);
  };

  const getUserList = async (skipSetFolders = false) => {
    switch (selectedType?.code) {
      case "shareholder": {
        const data = await fetchShareholders();
        const userList = data?.map((shareholder) => {
          const shareholderName =
            shareholder.type === "Individual"
              ? shareholder.preferred_name
                ? shareholder.preferred_name
                : `${shareholder.name}${shareholder.last_name ? " " + shareholder.last_name : ""}`
              : shareholder.legal_name;
          return {
            id: shareholder.id,
            shareholderType: shareholder.type,
            name: shareholderName,
            type: "SHAREHOLDER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        if (!skipSetFolders) setFolders(userList);
        break;
      }
      case "directors": {
        const { data } = await dispatch(fetchDirectors({ id })).unwrap();
        const userList = data
          ?.filter((director) =>
            director.appointments?.some(
              (appointment) => appointment.entity_id === id,
            ),
          )
          .map((director) => {
            const directorName = director.preferred_name
              ? director.preferred_name
              : `${director.name}${director.last_name ? " " + director.last_name : ""}`;
            return {
              id: director.id,
              shareholderType: director.type,
              name: directorName,
              type: "DIRECTOR",
            };
          });
        setSelectedType({ ...selectedType, subFolders: userList });
        if (!skipSetFolders) setFolders(userList);
        break;
      }
      case "officers": {
        const records = await fetchOfficersOrPowerOfAttorney("officer");
        const userList = records?.map((officer) => {
          const officerName = officer.preferred_name
            ? officer.preferred_name
            : `${officer.name}${officer.last_name ? " " + officer.last_name : ""}`;
          return {
            id: officer.id,
            name: officerName,
            type: "OFFICER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        if (!skipSetFolders) setFolders(userList);
        break;
      }
      case "power-of-attorney": {
        const records =
          await fetchOfficersOrPowerOfAttorney("power-of-attorney");
        const userList = records?.map((poa) => {
          const poaName = poa.preferred_name
            ? poa.preferred_name
            : `${poa.name}${poa.last_name ? " " + poa.last_name : ""}`;
          return {
            id: poa.id,
            name: poaName,
            type: "OFFICER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        if (!skipSetFolders) setFolders(userList);
        break;
      }
    }
  };

  const fetchShareholders = async () => {
    const { data } = await apiService(API_ROUTES.SHARE_HOLDERS, "GET", {
      replace: { id },
    });
    return data;
  };

  const fetchOfficersOrPowerOfAttorney = async (type) => {
    const {
      data: { records },
    } = await apiService(API_ROUTES.FETCH_OFFICERS, "GET", {
      replace: {
        entity_id: id,
        page: 1,
        pageSize: 100,
        searchValue: "",
        type,
        sortBy: "name",
        sortOrder: "ASC",
      },
      schema: z.object({
        page: z.number().optional(),
        pageSize: z.number().optional(),
        search: z.string().optional(),
        type: z.enum(["officer", "power-of-attorney"]),
        sortBy: z.enum(["name", "updatedAt", "role"]).optional(),
        sortOrder: z.enum(["ASC", "DESC"]).optional(),
      }),
    });
    return records;
  };

  const fetchRequests = async () => {
    let {
      data: { requests },
    } = await apiService(API_ROUTES.FETCH_REQUESTS, "GET", {
      replace: { page: 1, pageSize: 100, filter: "all", requestType: "AD_HOC" },
    });
    requests = requests.filter((req) => req.entityData?.id === id);
    setRequests(requests);
  };

  const handleClickService = (folder) => {
    if (folder?.name === "Ad Hoc Requests") {
      navigate(`requests/${folder?.id}`);
    } else {
      navigate(`services/${folder?.id}`);
    }
  };

  const reasonTypes = {
    SERVICE_QUERY: "Service Query",
    ADDITIONAL_SERVICE_REQUEST: "New / Additional Service Request",
    FEES_QUERY: "Contract / Fees query",
    TECH_SUPPORT: "Tech Support (IT/Helpdesk)",
    IMPLEMENTATION: "Implementation",
  };

  const handleClickType = (folder) => {
    if (selectedService?.name === "Core Entity Information") {
      if (folder?.isCategoryFolder) {
        setSelectedCategory(folder);
        return;
      }
      navigate(`${folder?.id}`);
    } else if (selectedService?.name === "Ad Hoc Requests") {
      const matchingKey = Object.entries(reasonTypes).find(
        ([, value]) => value.toLowerCase() === folder?.name?.toLowerCase(),
      )?.[0];
      if (matchingKey) navigate(`request-type/${matchingKey.toLowerCase()}`);
    } else {
      navigate(`process/${folder?.id}`);
    }
  };

  const handleClickPayslip = (folder) => {
    navigate(`payslips`);
  };

  const handleClickRequest = (folder) => {
    navigate(`${folder.id}`);
  };

  const handleClickServiceProcess = (process) => {
    setSelectedType(process);
    navigate(`${process.name}`);
  };

  const handleClickServiceSubFolder = (folder) => {
    setSelectedType(folder);
    navigate(`${folder.code}`);
  };

  const handleCategoryFolderClick = (folder) => {
    setSelectedCategory(folder);
  };

  const handleBackClick = () => {
    // Breadcrumb back should always go to the immediately previous level.
    // If a category folder is selected, back should simply clear it (stay on same route).
    if (selectedCategory) {
      setSelectedCategory(null);
      // Clear currently shown category folders immediately, then refetch.
      setFolders([]);
      setCategoryRefreshKey((k) => k + 1);
      return;
    }

    if (isPayslipFolder && type && service_id && process_id) {
      navigate(
        `/files/entity/${id}/services/${service_id}/${type}/process/${process_id}`,
      );
    } else if (service_id && type && request_type && request_id) {
      setSelectedRequest(null);
      navigate(
        `/files/entity/${id}/requests/${service_id}/${type}/request-type/${request_type}`,
      );
    } else if (type && service_id && (process_id || request_type)) {
      setSelectedProcess(null);
      setSelectedRequestType(null);
      navigate(
        `/files/entity/${id}/${selectedService?.name === "Ad Hoc Requests" ? "requests" : "services"}/${service_id}/${type}`,
      );
    } else if (type && service_id) {
      setSelectedType(null);
      navigate(
        `/files/entity/${id}/${selectedService?.name === "Ad Hoc Requests" ? "requests" : "services"}/${service_id}`,
      );
    } else if (service_id) {
      setSelectedService(null);
      navigate(`/files/entity/${id}`);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (!process_id) return;
    const process =
      selectedService?.name !== "Core Entity Information"
        ? selectedType?.process?.find((x) => x.id == process_id)
        : selectedType?.subFolders?.find((x) => x.id == process_id);
    setSelectedProcess(process);
    setProcessName(process?.name);
  }, [process_id, selectedService, selectedType]);

  useEffect(() => {
    if (!type || !folders || selectedType) return;
    if (selectedService?.name === "Ad Hoc Requests") {
      const typeSelected = folders?.find((f) => f.name == type);
      if (typeSelected) setSelectedType(typeSelected);
    }
  }, [type, folders, selectedType]);

  useEffect(() => {
    if (!request_type || selectedRequestType) return;
    const key = Object.keys(reasonTypes).find(
      (key) => key.toLowerCase() === request_type.toLowerCase(),
    );
    const requestType = requestTypeSubFolders?.find((x) => x.id == key);
    if (requestType) setSelectedRequestType(requestType);
  }, [request_type, selectedRequestType]);

  useEffect(() => {
    if (!request_id || selectedRequest) return;
    const request = requests?.find((x) => x.id == request_id);
    if (request) {
      setSelectedRequest({ ...request, name: request.request_id });
    }
  }, [request_id, requests, selectedRequest]);

  // Ensure selectedRequest has serviceData (e.g. for Payroll backend name in attachment table).
  useEffect(() => {
    if (!request_id || !selectedRequest?.id || selectedRequest?.serviceData)
      return;
    let cancelled = false;
    apiService(API_ROUTES.FETCH_REQUEST, "GET", {
      replace: { id: request_id },
    })
      .then(({ data }) => {
        if (!cancelled && data?.serviceData)
          setSelectedRequest((prev) =>
            prev ? { ...prev, serviceData: data.serviceData } : prev,
          );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [request_id, selectedRequest?.id]);

  const handleChangePage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleOpenRequest = () => {
    navigate(`/requests/${selectedRequest?.id}/conversation`);
  };

  const handleOpenProcess = () => {
    navigate(`/process/${selectedProcess?.id}/milestones`);
  };

  // Publish attachment files in Core Entity Info
  const promoteAttachmentsToEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.PROMOTE_ATTACHMENT_TO_ENTITY, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
          category: z.string(),
          subCategory: z.string(),
        }),
      });
    } catch (error) {
      throw new Error("Error promoting attachments to entity info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Publish attachment files in Core Entity Info
  const unpublishAttachmentsFromEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.UNPUBLISH_DOCUMENT, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
        }),
      });
    } catch (error) {
      throw new Error(
        "Error unpublishing attachments from entity info:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    states: {
      entity_detail,
      folders,
      loading,
      selectedService,
      processAttachments,
      selectedProcess,
      selectedType,
      selectedRequestType,
      selectedRequest,
      isPayslipFolder,
      selectedCategory,
      categoryFoldersLoading,
    },
    handleClickService,
    handleClickServiceProcess,
    handleClickServiceSubFolder,
    handleBackClick,
    handleClickType,
    handleCategoryFolderClick,
    setSelectedCategory,
    fileName,
    setFileName,
    dispatch,
    processName,
    first,
    rows,
    onPageChange: handleChangePage,
    handleClickRequest,
    handleOpenRequest,
    handleOpenProcess,
    handleClickPayslip,
    promoteAttachmentsToEntityInfo,
    unpublishAttachmentsFromEntityInfo,
    setCategoryFoldersRefetch,
  };
};

export default useFileDocuments;
